import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { InputText, Modal } from 'src/components';
import {
  ArrowLeft,
  InfoIconBeta,
  QuestionIcon,
  Rectangle,
} from 'src/components/Icons';
import { useEventBus, usePromptConfirm } from 'src/hooks';
import { ConsolidationQuestionAwareCollapsibleList } from 'src/sections/AwareCollapsibleList';
import { processConsolidationActions } from 'src/stores/actions/consolidations';
import { isNullOrEmptyArray } from 'src/util/arrays';
import * as Ui from './styles';
import {
  allConsolidationsToAltsIds,
  consolidationToAltsIds,
  getConsolidatedItems,
  getConsolidationSchema,
  transformIds,
} from './utils';

import getParsedPayload from './payloadParser';
import { ON_CLEAR_SELECTIONS } from 'src/hooks/EventBus/eventTypes';

export const CONSOLIDATION_SUCCESS = 'Configuração salva com sucesso';

function SimpleChartConsolidation({
  // identidade do cliente
  customerId,
  // identidade da pesquisa
  surveyId,
  // identidade da pergunta principal
  questionId,
  // Alternativas da pesquisa/pergunta atualmente selecionada
  fromServerConsolidations = [],
  // enunciado da pergunta principal
  questionTitle,
  // identifica se a modal está aberta
  isOpen = false,
  // propaga o evento de acionamento de fechar o modal
  onClose,
  // propaga o evento de acionamento de salvar os dados
  onSave,
  // propriedade de carregamento da requisição
  isLoading,
  // outros propriedades
  ...props
}) {
  const { theme } = props;

  // states

  // EventBus
  const eventBus = useEventBus();
  // Título da consolidação
  const [consolidationTitle, setConsolidationTitle] = useState('');
  // Modo de edição
  const [isEditMode, setIsEditMode] = useState(false);
  // Estado de ativação ou não da possibilidade de consolidar
  const [enableConsolidation, setEnableConsolidation] = useState(false);
  // Id's das alternativas selecionadas
  const [selectedAltsIds, setSelectedAltsIds] = useState([]);
  // Consolidações visuais
  const [consolidations, setConsolidations] = useState([]);
  // Índice da consolidação que está em modo de edição
  const [editModeConsolidationIndex, setEditModeConsolidationIndex] =
    useState(-1);
  // Valores selecionados
  const [selectedValues, setSelectedValues] = useState([]);
  // Consolidações deletadas (apenas os identificadores), armazena os ids das consolidações
  // pré-existentes (recuperadas do servidor). Sua utilidade será para diferenciar se um delete
  // ou PUT deve ser feito para atualização de consolidação.
  const [deletedConsolidations, setDeletedConsolidations] = useState([]);
  // Prompt config
  const [openConfirm, renderConfirm] = usePromptConfirm();
  // Controla a capacidade do usuáriode salvar uma configuração (active/disable) no botão concluir
  const [userCanSave, setUserCanSave] = useState(false);

  // refs
  const singleCollapsedListRef = useRef(null);
  const textInputRef = useRef(null); // hook criado para limpar value após input

  // redux
  const dispatch = useDispatch();

  function handleSave() {
    // open dialog to storing
    openConfirm(() => {
      // dados da configuração
      const { creates, updates, deletes } = getOnSaveConfigs();

      // parser: dados de interface para dados esperados pela api.
      const parsedConfigs = getParsedPayload(creates, updates, deletes);

      // action: Processa e lida com as requisições das modificações feitas pelo usuário.
      processConsolidationActions(
        parsedConfigs,
        customerId,
        surveyId,
        questionId,
        dispatch
      );
    });
  }

  function handleCancel() {
    close(false, false);
  }

  /**
   * Effect: controla o estado de disabled do botão concluir
   */
  useEffect(() => {
    // Caso esteja em edit mode, o usuário não pode salvar a configuração
    if (isEditMode) {
      setUserCanSave(false);
      return;
    }

    const { creates, updates, deletes } = getOnSaveConfigs();

    // Caso não existam dados a serem processados, o usuário não pode concluir a operação
    if (!isNullOrEmptyArray([...creates, ...updates, ...deletes])) {
      setUserCanSave(true);
    }
    // Se tiverem dados a serem processados porém o botão já está active, ele será desabilitado.
    else if (userCanSave) {
      setUserCanSave(false);
    }

  }, [consolidations, fromServerConsolidations, deletedConsolidations, userCanSave, isEditMode]);

  /**
   * effect: Seta as consolidações iniciais
   */
  useEffect(() => {
    // we dont need references
    setConsolidations(fromServerConsolidations.map((cons) => ({ ...cons })));
  }, [fromServerConsolidations]);

  /**
   * callback: Seta o estado de edit mode para true/false
   */
  const handleEditModeChanges = useCallback(
    (consolidationIndex) => {
      const newEditModeValue = !isEditMode;
      const findedConsolidationItem = consolidations[consolidationIndex];

      if (findedConsolidationItem) {
        const newConsolidations = [...consolidations];
        const { editMode, title } = newConsolidations[consolidationIndex];

        newConsolidations[consolidationIndex].editMode = !editMode;
        setConsolidations(newConsolidations);
        setEditModeConsolidationIndex(consolidationIndex);

        // Change consolidation title
        setConsolidationTitle(title);
        textInputRef.current.setText(title);
      }

      setIsEditMode(newEditModeValue);
    },
    [isEditMode, consolidations, textInputRef]
  );

  /**
   * callback: Remove uma consolidação
   */
  const handleOnDeleteConsolidation = useCallback(
    (consolidationIndex) => {
      const deletedConsolidation = consolidations[consolidationIndex];
      if (deletedConsolidation) {
        // Pre-existing consolidation
        if (deletedConsolidation.value !== undefined) {
          setDeletedConsolidations([
            ...deletedConsolidations,
            deletedConsolidation.value,
          ]);
        }

        const newConsolidations = [...consolidations];
        newConsolidations.splice(consolidationIndex, 1);

        setConsolidations(newConsolidations);
      }
    },
    [
      consolidations,
      setConsolidations,
      deletedConsolidations,
      setDeletedConsolidations,
    ]
  );

  function resetSelectedAltsProxy() {
    setSelectedValues([]);
    setSelectedAltsIds([]);
  }

  /**
   * callback: Cancela uma edição de consolidação
   */
  const handleOnLeaveEditMode = useCallback(() => {
    if (editModeConsolidationIndex !== -1) {
      const newConsolidationState = [...consolidations];
      newConsolidationState[editModeConsolidationIndex].editMode = false;

      setConsolidations(newConsolidationState);
      setIsEditMode(false);
      resetSelectedAltsProxy();
      setEditModeConsolidationIndex(-1);
      setConsolidationTitle('');
      textInputRef.current.clear();

      eventBus.notify(ON_CLEAR_SELECTIONS);
    }
  }, [consolidations, editModeConsolidationIndex, textInputRef, resetSelectedAltsProxy]);

  /**
   * Effects: Seta as alternativas que devem estar pré-selecionadas no modo de edição
   */
  useEffect(() => {
    if (
      editModeConsolidationIndex !== -1 &&
      !isNullOrEmptyArray(consolidations)
    ) {
      const values = [];
      const editModeConsolidation = consolidations[editModeConsolidationIndex];

      values.push(
        ...editModeConsolidation.alternatives.map((item) => item.value)
      );

      setSelectedAltsIds([...editModeConsolidation.alternatives]);
      setSelectedValues(values);
    }
  }, [consolidations, editModeConsolidationIndex, setSelectedValues, setSelectedAltsIds]);

  /**
   * Effects: Verifica se o botão de consolidação pode ser habilitado, caso sim, habilita-o ou desabilita-o
   * automaticamente.
   */
  useEffect(() => {
    const consolidationHasText = !!consolidationTitle.length;

    if (
      selectedAltsIds.length > 1 &&
      consolidationHasText &&
      !enableConsolidation
    ) {
      setEnableConsolidation(true);
      return;
    }

    if (
      (selectedAltsIds.length <= 1 || !consolidationHasText) &&
      !!enableConsolidation
    ) {
      setEnableConsolidation(false);
    }
  }, [selectedAltsIds, consolidationTitle, enableConsolidation]);

  /**
   * Callbacks: Retorna quais as ações que devem ser executadas ao concluir uma configuração
   * de consolidação. Retorna um objeto com as consolidações que devem ser criadas (POST),
   * atualizadas (PUT) e deletadas (DELETE)
   */
  const getOnSaveConfigs = useCallback(() => {
    // Evita processamento desnecessário
    if (isNullOrEmptyArray([...consolidations, ...fromServerConsolidations, ...deletedConsolidations])) {
      return {
        creates: [],
        updates: [],
        deletes: [],
      }
    }

    // Consolidações ainda visuais
    const newConsolidations = [
      ...consolidations.filter((cons) => typeof cons.value === 'undefined'),
    ];

    // Array com as consolidações deletadas, são filtradas pelo id de uma consolidação deletada
    const fromServerDeletedConsolidationsAltsIds = allConsolidationsToAltsIds(
      fromServerConsolidations.filter((scons) =>
        deletedConsolidations.includes(scons.value)
      )
    );

    // Casos de delete que serão entendidos como PUT, para evitar requisições
    // a mais desnecessárias.
    const deleteCasesToUpdate = [];

    // Casos de criação de consolidações
    const creates = [
      // Adiciona apenas as consolidações que não possuem id, significa que é uma nova consolidação.
      ...(deletedConsolidations.length
        ? newConsolidations.filter((cons) => {
            const ids = consolidationToAltsIds(cons);
            const inDeletedConsolidation = ids.findIndex((id) =>
              fromServerDeletedConsolidationsAltsIds.includes(id)
            );

            if (inDeletedConsolidation !== -1) {
              deleteCasesToUpdate.push({
                value: fromServerConsolidations.find((scons) =>
                  scons.alternatives.some(
                    ({ value }) =>
                      value ===
                      fromServerDeletedConsolidationsAltsIds[
                        inDeletedConsolidation
                      ]
                  )
                ).value,
                ...cons,
              });
            } else return true;
          })
        : newConsolidations),
    ];

    // Casos de edições de consolidações
    const updates = consolidations
      .filter((cons) => typeof cons.value !== undefined)
      .filter((cons) => {
        const findedFromServer = fromServerConsolidations.find(
          (scons) => scons.value === cons.value
        );

        // Edit case
        if (findedFromServer) {
          const consAltsIds = consolidationToAltsIds(cons);
          const findedFromServerAltsIds =
            consolidationToAltsIds(findedFromServer);
          const altsChanged =
            transformIds(findedFromServerAltsIds) !== transformIds(consAltsIds);
          const titleChanged = findedFromServer.title !== cons.title;

          if (titleChanged) {
            cons.oldTitle = findedFromServer.title;
          }

          return altsChanged || titleChanged;
        }

        // default
        return false;
      });

    updates.push(...deleteCasesToUpdate);

    // São deletes consolidações que possuiam id definido e não possuem alternativas consolidadas
    // pertencentes a outra consolidação
    const deletes = [
      ...fromServerConsolidations.filter(
        (cons) =>
          deletedConsolidations.includes(cons.value) &&
          !updates.map((ucons) => ucons.value).includes(cons.value)
      ),
    ];

    return {
      creates,
      updates,
      deletes,
    };
  }, [consolidations, deletedConsolidations, fromServerConsolidations]);

  /**
   * Events: Seta as alternativas atualmente selecionadas
   */
  function handleAlternativeSelection(_, altsId) {
    const altsArray = Array.isArray(altsId);

    if (altsArray) {
      setSelectedAltsIds([...altsId]);
    }
  }
  /**
   * Events: Seta o título da consolidação definido pelo usuário
   */
  function handleConsolidationTitleChange(newTitle) {
    setConsolidationTitle(newTitle);
  }

  /**
   * Callbacks: disparado ao clicar em "consolidar", irá armazenar a configuração de consolidação selecionada.
   */
  const handleAlternativeConsolidation = useCallback(() => {
    if (!isNullOrEmptyArray(selectedAltsIds) && consolidationTitle.length) {
      // Retorna os items que estão consolidados
      const consolidatedItems = getConsolidatedItems(
        selectedAltsIds,
        consolidations
      );

      // Filtro: Filtra os itens que não fazem parte de uma consolidação
      function filterExistingConsolidations(item) {
        const isConsolidatedItem = consolidatedItems.some(
          (citem) => citem.value === item.value
        );
        return !isConsolidatedItem;
      }

      const newConsolidations = [...consolidations];

      // Caso não esteja no modo de edição, significa que é uma nova consolidação
      if (editModeConsolidationIndex === -1) {
        const toConsolidateItems = getConsolidationSchema({
          alternatives: [
            ...selectedAltsIds
              .filter(filterExistingConsolidations)
              .map((item) => ({ ...item })),
          ],
          title: consolidationTitle,
          editMode: false,
        });

        newConsolidations.push(toConsolidateItems);
      } else {
        const toConsolidateItems = getConsolidationSchema({
          value:
            newConsolidations[editModeConsolidationIndex].value || undefined,
          alternatives: [...selectedAltsIds.map((item) => ({ ...item }))],
          title: consolidationTitle,
          editMode: false,
        });

        newConsolidations[editModeConsolidationIndex] = toConsolidateItems;

        setIsEditMode(false);
        setEditModeConsolidationIndex(-1);
      }

      // limpando estado após input do value
      setConsolidationTitle('');
      // chamando metodo do componente inputText para limpar value do input
      textInputRef.current.clear();

      // Notifica a ação de consolidar
      eventBus.notify(ON_CLEAR_SELECTIONS);

      setConsolidations(newConsolidations);
      // Reset consolidations selections
      resetSelectedAltsProxy();
    }
  }, [
    consolidations,
    selectedAltsIds,
    consolidationTitle,
    editModeConsolidationIndex,
    setConsolidationTitle,
    resetSelectedAltsProxy,
  ]);

  /**
   * callbacks: fecha esta configuração
   */
  const close = useCallback(
    (saving, editing) => {
      // propagar evento de salvar e fechar
      if (saving && onSave) {
        onSave(editing);
      }
      if (!saving && onClose) {
        onClose();
      }
    },
    [onClose, onSave]
  );

  // Renderiza a Ui do modo de edição de uma consolidação
  const renderEditModeInterface = useCallback(() => {
    const currentEditModeConsolidation = consolidations.find(
      (consolidation) => consolidation.editMode
    );
    // Título da consolidação
    const cTitle =
      (currentEditModeConsolidation && currentEditModeConsolidation.title) ||
      '';
    const label = `Edição de consolidação - ${cTitle}`;

    return (
      <>
        <Ui.ComebackWrapper onClick={handleOnLeaveEditMode}>
          <ArrowLeft theme={theme} />
          <Rectangle theme={theme} />
          {'Voltar'}
        </Ui.ComebackWrapper>
        <Ui.LabelSmall withIcon>
          <InfoIconBeta theme={theme} />
          {label}
        </Ui.LabelSmall>
      </>
    );
  }, [consolidations]);

  function renderContainer() {
    return (
      <Ui.AbsoluteContainer>
        <Ui.ToolTip>
          {isEditMode ? (
            renderEditModeInterface()
          ) : (
            <Ui.LabelSmall>
              {
                'Ao consolidar dados você cria uma configuração virtual para sua análise, mas não alterar os dados original da sua análise'
              }
            </Ui.LabelSmall>
          )}
        </Ui.ToolTip>
        <Ui.Container>
          <Ui.FixedItem>
            <Ui.Label withIcon>
              <InfoIconBeta theme={theme} />
              {'Pergunta principal'}
            </Ui.Label>
            <ConsolidationQuestionAwareCollapsibleList
              selectableItem
              selectedItems={selectedValues}
              multCheck
              checkedTitle
              boldedTitle
              expansible
              ref={singleCollapsedListRef}
              onChangeSelectedAlternativesIds={handleAlternativeSelection}
              customerId={customerId}
              surveyId={surveyId}
              questionId={questionId}
              questionTitle={questionTitle}
              consolidations={consolidations}
              handleEditModeChanges={handleEditModeChanges}
              onDeleteConsolidation={handleOnDeleteConsolidation}
            />
          </Ui.FixedItem>
          <Ui.TitleGroup>
            <Ui.Label withIcon>
              <InfoIconBeta theme={theme} />
              {'Novo nome da alternativa'}
            </Ui.Label>
            <Ui.InputsWrapper>
              <InputText
                onChange={handleConsolidationTitleChange}
                value={consolidationTitle}
                placeholder={'INFORME O NOVO NOME'}
                ref={textInputRef}
              />
              <Ui.ConsolidateBtn
                title={'CONSOLIDAR'}
                clickable={enableConsolidation}
                onClick={handleAlternativeConsolidation}
              />
            </Ui.InputsWrapper>
          </Ui.TitleGroup>
        </Ui.Container>
      </Ui.AbsoluteContainer>
    );
  }

  function renderModalBody() {
    if (customerId && surveyId && questionId) {
      return renderContainer();
    }
  }

  // Component
  return (
    <Ui.Wrapper okBtnDisabled={!userCanSave}>
      <Modal
        isOpen={isOpen}
        title={'Consolidação de alternativas'}
        okBtnTitle={'Concluir'}
        cancelBtnTitle={'Cancelar'}
        onOk={handleSave}
        onCancel={handleCancel}
        isLoading={isLoading}
        withLoadingSpinner
      >
        {renderModalBody()}
        {renderConfirm(QuestionIcon, 'Deseja salvar esta configuração?')}
      </Modal>
    </Ui.Wrapper>
  );
}

export default SimpleChartConsolidation;
